import { useMutation, useQuery } from '@apollo/client';
import {
  Google as GoogleIcon,
  Javascript as JavascriptIcon,
  SpinnerIndicators as Spinner,
} from '@clearkit/icons';
import { CKBox, CKTab, CKTabList, CKTag } from 'clearkit';
import { useEffect, useState } from 'react';

import BooleanToggleSection from '~/components/configuration/BooleanToggleSection';
import Integration from '~/components/ConfigurationIntegration';
import ConfigurationLogo from '~/components/ConfigurationLogo';
import TestIntegrationButton from '~/components/ConfigurationTestIntegrationButton';
import JavaScriptReferrers from '~/components/JavaScriptReferrers';
import LoadingArea from '~/components/LoadingArea';
import { PageLayoutTabs } from '~/components/PageLayout';
import { PAGES } from '~/constants/pages';
import ToastsContainer from '~/containers/Toasts';
import { useEntitlements } from '~/hooks/account';
import * as client from '~/lib/client';
import { tagsjsSnippet } from '~/lib/configuration';
import history from '~/lib/history';

import mutation from './mutation.graphql';
import query from './query.graphql';
import RevealFeatureGuard from './RevealFeatureGuard';

const serviceForTab = (tab: number) => {
  switch (tab) {
    case 1:
      return 'google';
    default:
      return 'clearbit.js';
  }
};

const TagsJs = () => {
  const { hasAccessSettingsIntegrations } = useEntitlements();

  const [tab, setTab] = useState(0);
  const [referrers, setReferrers] = useState([]);
  const [settings, setSettings] = useState({
    reveal_async: false,
    reveal: false,
    tracking: false,
    tracking_cookies: false,
  });

  async function updateSettings(update: any) {
    const newSettings = {
      ...settings,
      ...update,
    };

    // optimistic update
    setSettings(newSettings);

    const res = await client.updateTagsSettings(newSettings);
    setSettings(res.settings);
  }

  const { data, loading } = useQuery(query, { returnPartialData: true });

  const [updateAccount, { loading: mutationRunning }] = useMutation(mutation, {
    onCompleted: (data) => {
      if (data.errors?.length) {
        ToastsContainer.addError('There was an error saving your settings.');
      } else {
        ToastsContainer.addSuccess('Configuration saved');
      }
    },
  });

  const {
    // @ts-ignore
    account: { publishableToken, remoteAllowedReferrers = [] } = {},
  } = data;

  useEffect(() => {
    setReferrers(
      remoteAllowedReferrers.filter(
        (referrer: string) => !referrer.includes('*'),
      ),
    );
  }, [remoteAllowedReferrers]);

  useEffect(() => {
    if (!publishableToken) return;

    async function fetchData() {
      const res = await client.getTagsSettings();
      setSettings(res.settings);
    }
    fetchData();
  }, [publishableToken]);

  const handleSaveReferrers = async (newReferrers: string[]) => {
    updateAccount({
      variables: {
        input: { allowedReferrers: newReferrers },
      },
    });
  };

  const breadcrumbProps = PAGES.CONFIGURATIONS.CONFIGURATION;

  const title = 'Clearbit JavaScript';

  if (loading && !data) return <LoadingArea loading />;

  return (
    <PageLayoutTabs index={tab} onChange={setTab}>
      <PageLayoutTabs.Header>
        <PageLayoutTabs.Tile variant="clear">
          <ConfigurationLogo type="clearbit" />
        </PageLayoutTabs.Tile>
        {hasAccessSettingsIntegrations ? (
          <PageLayoutTabs.Heading>{title}</PageLayoutTabs.Heading>
        ) : (
          <PageLayoutTabs.Breadcrumb
            breadcrumbs={[
              {
                title: breadcrumbProps.TITLE,
                path: breadcrumbProps.PATH,
              },
            ]}
          >
            {title}
          </PageLayoutTabs.Breadcrumb>
        )}
        <PageLayoutTabs.SubHeading>
          Install JavaScript snippet
        </PageLayoutTabs.SubHeading>
        <CKTabList>
          <CKTab
            icon={<JavascriptIcon height={16} width={16} />}
            label="Use Javascript snippet"
          >
            Use Javascript snippet
          </CKTab>
          <CKTab
            icon={<GoogleIcon height={16} width={16} />}
            label="Use Google Tag Manager"
          >
            Use Google Tag Manager
          </CKTab>
        </CKTabList>
      </PageLayoutTabs.Header>

      <PageLayoutTabs.Body>
        <PageLayoutTabs.Content className="space-y-8">
          <div className="space-y-6">
            {tab === 0 && (
              <Integration
                copyText={
                  <span>
                    Copy the code snippet and paste it in your website code
                    above the <CKTag>{`</head>`}</CKTag> tag.
                  </span>
                }
                integrationText={
                  <span>
                    Add this snippet to your website html to identify your
                    anonymous web traffic and track website events in Clearbit.
                    Visit our{' '}
                    <a
                      className="ck-link"
                      href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      installation guide
                    </a>{' '}
                    for more instructions.
                  </span>
                }
                snippet={tagsjsSnippet(publishableToken, false)}
                textAreaHeight={30}
              />
            )}
            {tab === 1 && (
              <Integration
                copyText={
                  <span>
                    Copy the code snippet and paste it in to your Google Tag
                    configuration.
                  </span>
                }
                integrationText={
                  <span>
                    Create a new tag in GTM with the following snippet to
                    identify your anonymous web traffic and track website events
                    in Clearbit. Visit our{' '}
                    <a
                      className="ck-link"
                      href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      installation guide
                    </a>{' '}
                    for more instructions.
                  </span>
                }
                snippet={tagsjsSnippet(publishableToken, true)}
                textAreaHeight={30}
              />
            )}
            <TestIntegrationButton
              className="flex flex-row-reverse items-center justify-end gap-4"
              navigate={() => history.push('/')}
              service={serviceForTab(tab)}
            />
            <JavaScriptReferrers
              loading={mutationRunning}
              onSave={handleSaveReferrers}
              referrers={referrers}
            />
          </div>
          <div>
            <h2 className="mb-2 text-base font-medium text-gray-600">
              Settings
            </h2>

            <p className="mb-6 text-sm text-gray-500">
              Enable or disable tracking and personalization settings. Refer to
              the{' '}
              <a
                className="ck-link"
                href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                rel="noopener noreferrer"
                target="_blank"
              >
                installation and usage guide
              </a>{' '}
              for additional information on these settings.
            </p>

            {settings ? (
              <div className="space-y-6">
                <CKBox className="divide-y divide-gray-200">
                  <BooleanToggleSection
                    checked={settings.tracking}
                    description={
                      <div className="text-sm text-gray-600 space-y-4">
                        <p>
                          Track visitors site activity, send user and account
                          information to Clearbit, and more.
                        </p>
                        <p>
                          <strong>Warning: </strong>If you are sending activity
                          to Clearbit via Segment, this may cause Clearbit to
                          double count activity.{' '}
                          <a
                            className="ck-link"
                            href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Learn more
                          </a>
                          .
                        </p>
                      </div>
                    }
                    id="settings_tracking"
                    onChange={(checked) => {
                      updateSettings({
                        tracking: checked,
                      });
                    }}
                    title="Tracking"
                  />

                  {settings.tracking && (
                    <BooleanToggleSection
                      checked={settings.tracking_cookies}
                      description={
                        <p className="text-sm text-gray600">
                          Drop a cookie to track user activity across the site.{' '}
                          <a
                            className="ck-link"
                            href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            Learn more
                          </a>
                          .
                        </p>
                      }
                      id="settings_cookie_tracking"
                      onChange={(checked) => {
                        updateSettings({
                          tracking_cookies: checked,
                        });
                      }}
                      title="Cookie Tracking"
                    />
                  )}
                </CKBox>

                {!hasAccessSettingsIntegrations ? (
                  <RevealFeatureGuard>
                    <CKBox className="mb-4 divide-y divide-gray-200">
                      <BooleanToggleSection
                        checked={settings.reveal}
                        description={
                          <p className="text-base text-gray-500">
                            Surface reveal data to the front end of your website
                            to power personalization.{' '}
                            <a
                              className="ck-link"
                              href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              Learn more
                            </a>
                            .
                          </p>
                        }
                        id="settings_reveal"
                        onChange={(checked) => {
                          updateSettings({
                            reveal: checked,
                          });
                        }}
                        title="Reveal data"
                      />

                      {settings.reveal && (
                        <BooleanToggleSection
                          checked={!settings.reveal_async}
                          description={
                            <p className="text-base text-gray-500">
                              Block page load until after a reveal response is
                              available. <br />
                              Warning: this may increase your site load time.
                              Use with caution.{' '}
                              <a
                                className="ck-link"
                                href="https://help.clearbit.com/hc/en-us/articles/360063559573"
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                Learn more
                              </a>
                              .
                            </p>
                          }
                          disabled={!settings.reveal}
                          id="settings_reveal_async"
                          onChange={(checked) => {
                            updateSettings({
                              reveal_async: !checked,
                            });
                          }}
                          title="Synchronous Reveal data"
                        />
                      )}
                    </CKBox>
                  </RevealFeatureGuard>
                ) : null}
              </div>
            ) : (
              <CKBox
                className="flex items-center p-6"
                variant={CKBox.Variant['tinted-frame']}
              >
                <Spinner></Spinner>
              </CKBox>
            )}
          </div>
        </PageLayoutTabs.Content>
      </PageLayoutTabs.Body>
    </PageLayoutTabs>
  );
};

export default TagsJs;
